import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  AlarmClock,
  Plus,
  UserPlus,
  Pencil,
  Trash2,
  Search,
  Check,
  Clock,
} from "lucide-react";
import useSearchPagination from "../../../hooks/useSearchPagination";
import ShiftSwapModal from "./ShiftSwapModal";
import { API_URL } from "../../../api/api";
import { T } from "../../../components/em/tokens";

import { confirmDialog } from "../../../components/em/confirm";
import { toast } from "../../../utils/toast";
const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  // .main-header is a sticky element pinned at z-index: 1000 — at 400 this
  // overlay (and the modal's own header/title inside it) rendered BELOW it,
  // invisibly painted over for the top ~100px band the nav bar occupies.
  zIndex: 1200,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};
const modalCard = {
  width: "min(460px,100%)",
  background: "var(--em-bg-card)",
  borderRadius: 14,
  overflow: "hidden",
  boxShadow: "0 20px 40px -12px rgba(0,0,0,0.25)",
};
const fieldLabel = { font: `500 13px ${T.font}`, color: T.fg1, marginBottom: 8, display: "block" };
const fieldInput = {
  width: "100%",
  padding: "11px 14px",
  border: `1px solid ${T.border}`,
  borderRadius: 10,
  font: `400 14px ${T.font}`,
  color: T.fg1,
  outline: "none",
  boxSizing: "border-box",
  background: "var(--em-bg-card)",
};

const ShiftManagement = () => {
  const baseURL = `${API_URL}/api/settings/shifts`;
  const token = localStorage.getItem("token");
  const [shifts, setShifts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [form, setForm] = useState({ id: null, name: "", start_time: "", end_time: "", working_days: [] });
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedShift, setSelectedShift] = useState("");

  useEffect(() => {
    fetchShifts();
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchShifts = async () => {
    try {
      const res = await axios.get(baseURL, { headers: { Authorization: `Bearer ${token}` } });
      const shiftsData = res.data.map((s) => ({
        ...s,
        working_days: Array.isArray(s.working_days)
          ? s.working_days
          : s.working_days
            ? s.working_days.split(",")
            : [],
      }));
      setShifts(shiftsData);
    } catch (err) {
      console.error("Error fetching shifts:", err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${baseURL}/team-members`, { headers: { Authorization: `Bearer ${token}` } });
      setEmployees(res.data);
    } catch (err) {
      console.error("Error fetching employees", err);
    }
  };

  const handleDayToggle = (day) => {
    setForm((prev) => {
      const exists = prev.working_days.includes(day);
      return {
        ...prev,
        working_days: exists ? prev.working_days.filter((d) => d !== day) : [...prev.working_days, day],
      };
    });
  };

  const handleSaveShift = async () => {
    if (!form.name || !form.start_time || !form.end_time) {
      toast.error("Please fill all fields");
      return;
    }
    const authH = { headers: { Authorization: `Bearer ${token}` } };
    try {
      if (form.id) {
        await axios.put(`${baseURL}/${form.id}`, form, authH);
        toast.success("Shift updated successfully");
      } else {
        await axios.post(baseURL, form, authH);
        toast.success("Shift created successfully");
      }
      setForm({ id: null, name: "", start_time: "", end_time: "", working_days: [] });
      setShowCreateModal(false);
      fetchShifts();
    } catch (err) {
      console.error(err);
      toast.error("Error saving shift");
    }
  };

  const handleShiftSwap = async ({ employee1, employee2, shift1, shift2, dates }) => {
    try {
      await axios.post(`${baseURL}/swap`, { employee1, employee2, shift1, shift2, dates }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Shifts swapped successfully!");
      setShowSwapModal(false);
      fetchShifts();
    } catch (err) {
      console.error(err);
      toast.error("Error swapping shifts");
    }
  };

  const handleDeleteShift = async (id) => {
    if (!(await confirmDialog("Delete this shift?"))) return;
    try {
      await axios.delete(`${baseURL}/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Shift deleted successfully");
      fetchShifts();
    } catch (err) {
      console.error(err);
      toast.error("Error deleting shift");
    }
  };

  const handleEditShift = (shift) => {
    setForm({
      id: shift.id,
      name: shift.name,
      start_time: shift.start_time,
      end_time: shift.end_time,
      working_days: shift.working_days || [],
    });
    setShowCreateModal(true);
  };

  const handleAssignShift = async () => {
    if (!selectedEmployee || !selectedShift) {
      toast.error("Please select both employee and shift");
      return;
    }
    try {
      await axios.post(`${baseURL}/assign`, { employee_id: selectedEmployee, shift_id: selectedShift }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Shift assigned successfully!");
      setSelectedEmployee("");
      setSelectedShift("");
      setShowAssignModal(false);
      fetchShifts();
    } catch (err) {
      console.error(err);
      toast.error("Error assigning shift");
    }
  };

  const formatTime = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":");
    const hour = parseInt(h);
    const suf = hour >= 12 ? "PM" : "AM";
    const adj = hour % 12 || 12;
    return `${adj}:${m} ${suf}`;
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return "";
    const s = new Date(`1970-01-01T${start}`);
    const e = new Date(`1970-01-01T${end}`);
    let diff = (e - s) / 3600000;
    if (diff < 0) diff += 24;
    return `${diff} hrs`;
  };

  const { filteredData, currentData, totalPages } = useSearchPagination(
    shifts || [],
    searchTerm,
    currentPage
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <div style={{ width: 42, height: 42, borderRadius: 12, background: T.orange50, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <AlarmClock size={20} color={T.orange} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: `700 20px ${T.font}`, color: T.fg1, letterSpacing: "-0.02em" }}>Shift Management</div>
          <div style={{ font: `400 12px ${T.font}`, color: T.fg5 }}>Define and assign work shifts for your organization</div>
        </div>
        <button onClick={() => setShowAssignModal(true)} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 16px", borderRadius: 10, border: `1px solid ${T.border}`, cursor: "pointer", background: "var(--em-bg-card)", color: T.fg1, font: `600 13px ${T.font}` }}>
          <UserPlus size={15} /> Assign Shift
        </button>
        <button onClick={() => setShowCreateModal(true)} style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 10, border: 0, cursor: "pointer", background: T.gradient, color: "#fff", font: `700 13px ${T.font}`, boxShadow: T.shadowBrand }}>
          <Plus size={15} /> Create Shift
        </button>
      </div>

      {/* Search + total */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <div style={{ position: "relative", maxWidth: 280, flex: 1 }}>
          <Search size={13} color={T.fg7} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
          <input
            type="text"
            placeholder="Search shifts..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={{ width: "100%", padding: "9px 12px 9px 30px", border: `1px solid ${T.border}`, borderRadius: 8, font: `400 13px ${T.font}`, outline: "none", boxSizing: "border-box" }}
          />
        </div>
        <span style={{ font: `400 13px ${T.font}`, color: T.fg5, marginLeft: "auto" }}>
          Total Shifts: <strong style={{ color: T.fg1 }}>{filteredData.length}</strong>
        </span>
      </div>

      {/* Shift cards */}
      {currentData.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", font: `400 14px ${T.font}`, color: T.fg5, background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 14 }}>
          No shifts found
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
          {currentData.map((shift) => (
            <div key={shift.id} style={{ background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 14, padding: 18, boxShadow: T.shadowSm, position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div style={{ width: 34, height: 34, borderRadius: 9, background: T.orange50, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Clock size={16} color={T.orange} />
                </div>
                <div style={{ font: `700 16px ${T.font}`, color: T.fg1 }}>{shift.name}</div>
              </div>

              {[
                ["Timing", `${formatTime(shift.start_time)} – ${formatTime(shift.end_time)}`],
                ["Duration", calculateDuration(shift.start_time, shift.end_time)],
                ["Break", "1h"],
              ].map(([label, value]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", font: `400 13px ${T.font}` }}>
                  <span style={{ color: T.fg5 }}>{label}</span>
                  <span style={{ color: T.fg1, fontWeight: 500 }}>{value}</span>
                </div>
              ))}

              <div style={{ marginTop: 10 }}>
                <div style={{ font: `500 12px ${T.font}`, color: T.fg5, marginBottom: 6 }}>Working Days:</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {shift.working_days?.length ? (
                    shift.working_days.map((day, index) => (
                      <span key={index} style={{ padding: "3px 9px", borderRadius: 7, background: T.orangeSoft, color: T.orange, font: `600 11px ${T.font}` }}>
                        {day}
                      </span>
                    ))
                  ) : (
                    <span style={{ color: T.fg7, font: `400 12px ${T.font}` }}>—</span>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", gap: 6, marginTop: 16 }}>
                <button onClick={() => handleEditShift(shift)} style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 12px", borderRadius: 8, border: `1px solid ${T.border}`, background: "var(--em-bg-card)", color: T.fg1, font: `600 13px ${T.font}`, cursor: "pointer" }}>
                  <Pencil size={13} /> Edit
                </button>
                <button onClick={() => handleDeleteShift(shift.id)} style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "8px 12px", borderRadius: 8, border: "1px solid #fecaca", background: T.danger50, color: T.danger, font: `600 13px ${T.font}`, cursor: "pointer" }}>
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 16 }}>
          {[...Array(totalPages)].map((_, i) => (
            <button key={i} onClick={() => setCurrentPage(i + 1)} style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${currentPage === i + 1 ? T.orange : T.border}`, background: currentPage === i + 1 ? T.bgBlack : "#fff", color: currentPage === i + 1 ? "#fff" : T.fg1, cursor: "pointer", font: `${currentPage === i + 1 ? 700 : 500} 13px ${T.font}` }}>
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Create / Edit Modal */}
      {showCreateModal && (
        <div style={overlay} onClick={() => setShowCreateModal(false)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.divider}` }}>
              <div style={{ font: `700 16px ${T.font}`, color: T.fg1 }}>{form.id ? "Edit Shift" : "Create Shift"}</div>
            </div>
            <div style={{ padding: "20px 22px" }}>
              <div style={{ marginBottom: 16 }}>
                <label style={fieldLabel}>Shift Name</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={fieldInput} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={fieldLabel}>Start Time</label>
                  <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} style={fieldInput} />
                </div>
                <div>
                  <label style={fieldLabel}>End Time</label>
                  <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} style={fieldInput} />
                </div>
              </div>
              <div>
                <label style={fieldLabel}>Working Days</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {days.map((day) => {
                    const on = form.working_days.includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => handleDayToggle(day)}
                        style={{ padding: "7px 12px", borderRadius: 8, border: `1px solid ${on ? T.orange : T.border}`, background: on ? T.orangeSoft : "#fff", color: on ? T.orange : T.fg4, font: `600 12px ${T.font}`, cursor: "pointer" }}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div style={{ padding: "12px 22px", borderTop: `1px solid ${T.divider}`, display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setShowCreateModal(false)} style={{ padding: "9px 16px", borderRadius: 8, border: `1px solid ${T.border}`, cursor: "pointer", background: "var(--em-bg-card)", font: `600 14px ${T.font}`, color: T.fg1 }}>Cancel</button>
              <button onClick={handleSaveShift} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 20px", borderRadius: 8, border: 0, cursor: "pointer", background: T.gradient, color: "#fff", font: `600 14px ${T.font}`, boxShadow: T.shadowBrand }}>
                <Check size={15} /> {form.id ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSwapModal && (
        <ShiftSwapModal employees={employees} shifts={shifts} onClose={() => setShowSwapModal(false)} onSwap={handleShiftSwap} />
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <div style={overlay} onClick={() => setShowAssignModal(false)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.divider}` }}>
              <div style={{ font: `700 16px ${T.font}`, color: T.fg1 }}>Assign Shift</div>
            </div>
            <div style={{ padding: "20px 22px" }}>
              <div style={{ marginBottom: 16 }}>
                <label style={fieldLabel}>Select Employee</label>
                <select value={selectedEmployee} onChange={(e) => setSelectedEmployee(e.target.value)} style={fieldInput}>
                  <option value="">Select Employee</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={fieldLabel}>Select Shift</label>
                <select value={selectedShift} onChange={(e) => setSelectedShift(e.target.value)} style={fieldInput}>
                  <option value="">Select Shift</option>
                  {shifts.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ padding: "12px 22px", borderTop: `1px solid ${T.divider}`, display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setShowAssignModal(false)} style={{ padding: "9px 16px", borderRadius: 8, border: `1px solid ${T.border}`, cursor: "pointer", background: "var(--em-bg-card)", font: `600 14px ${T.font}`, color: T.fg1 }}>Cancel</button>
              <button onClick={handleAssignShift} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 20px", borderRadius: 8, border: 0, cursor: "pointer", background: T.gradient, color: "#fff", font: `600 14px ${T.font}`, boxShadow: T.shadowBrand }}>
                <Check size={15} /> Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftManagement;
